import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import SEO, {
  generateWebPageSchema,
  generateItemListSchema,
} from "../components/SEO";
import Nav from "../components/Nav";
import ArticleImage from "../components/ArticleImage";
import { db } from "../firebase";
import {
  articleCategoryConfig,
  getArticleCategoryBySlug,
  getArticleCategoryUrl,
  toAbsoluteUrl,
} from "../utils/seoPaths";
import {
  filterPublishedArticles,
  getPublicationDate,
} from "../utils/articlePublishing";

const Articles = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Gate Prerender until we finish loading + meta commits
  useEffect(() => {
    if (typeof window !== "undefined") window.prerenderReady = false;
  }, []);

  // Load articles from Firebase on component mount
  useEffect(() => {
    loadArticles();
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get("search");
    const categoryParam = urlParams.get("category");
    const pathCategory = categorySlug
      ? getArticleCategoryBySlug(categorySlug)?.slug
      : null;

    if (pathCategory) {
      setActiveFilter(pathCategory);
    } else if (categoryParam && articleCategoryConfig[categoryParam]) {
      setActiveFilter(categoryParam);
    } else {
      setActiveFilter("all");
    }

    setSearchTerm(searchParam || "");
  }, [categorySlug, location.search]);

  // Signal prerender once the list is loaded so bots get the final HTML
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!loading) {
      const t = setTimeout(() => {
        window.prerenderReady = true;
      }, 60);
      return () => clearTimeout(t);
    }
  }, [loading]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "articles"),
        where("date", "<=", getPublicationDate()),
        orderBy("date", "desc")
      );
      const querySnapshot = await getDocs(q);
      const articlesData = [];
      querySnapshot.forEach((doc) => {
        articlesData.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setArticles(filterPublishedArticles(articlesData));
    } catch (error) {
      console.error("Error loading articles:", error);
      // Fallback to default articles if Firebase fails
      setArticles([]);
    }
    setLoading(false);
  };

  const categories = [
    { id: "all", label: "All Articles", count: articles.length },
    {
      id: "unsung-heroes",
      label: "Unsung Heroes",
      count: articles.filter((a) => a.category === "unsung-heroes").length,
    },
    {
      id: "stories-of-recovery",
      label: "Stories of Recovery",
      count: articles.filter((a) => a.category === "stories-of-recovery")
        .length,
    },
    {
      id: "blog-posts",
      label: "Blog Posts",
      count: articles.filter((a) => a.category === "blog-posts").length,
    },
  ];

  const filteredArticles = articles.filter((article) => {
    const matchesFilter =
      activeFilter === "all" || article.category === activeFilter;
    const matchesSearch =
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.author.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "unsung-heroes":
        return "bg-emerald-500";
      case "stories-of-recovery":
        return "bg-purple-500";
      case "blog-posts":
        return "bg-cyan-500";
      default:
        return "bg-gray-500";
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "unsung-heroes":
        return "Unsung Heroes";
      case "stories-of-recovery":
        return "Recovery Stories";
      case "blog-posts":
        return "Blog Posts";
      default:
        return "Article";
    }
  };

  const currentCategory = activeFilter === "all" ? null : articleCategoryConfig[activeFilter];
  const seoTitle = currentCategory
    ? `${currentCategory.label} in New Hampshire`
    : "Recovery Stories, Unsung Heroes, and Blog Articles in New Hampshire";
  const seoDescription = currentCategory
    ? `Browse ${currentCategory.shortLabel.toLowerCase()} from Recovery Compass NH. Read real recovery stories, expert perspectives, and community reporting from New Hampshire.`
    : "Inspiring recovery stories, unsung hero spotlights, and expert advice on addiction recovery. Real stories of hope and healing in New Hampshire.";
  const canonicalUrl = toAbsoluteUrl(
    currentCategory ? getArticleCategoryUrl(activeFilter) : "/articles"
  );
  const breadcrumbs = [
    { name: "Home", url: "https://www.recoverycompassnh.com/" },
    {
      name: "Articles",
      url: "https://www.recoverycompassnh.com/articles",
    },
  ];

  if (currentCategory) {
    breadcrumbs.push({ name: currentCategory.label, url: canonicalUrl });
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        ogType="website"
        ogImage="https://www.recoverycompassnh.com/articles.png"
        ogImageAlt="Recovery stories and unsung heroes articles from New Hampshire"
        breadcrumbs={breadcrumbs}
        schema={[
          generateWebPageSchema({
            title: seoTitle,
            description: seoDescription,
            url: canonicalUrl,
            aboutName: "Addiction Recovery Stories",
            aboutDescription:
              "Personal narratives and profiles of recovery and mental health advocates in New Hampshire",
          }),
          ...(articles.length > 0
            ? [
                generateItemListSchema(
                  articles.slice(0, 10),
                  "Latest Recovery Stories & Articles"
                ),
              ]
            : []),
        ]}
      />

      <Nav />

      {/* Header Section */}
      <div className="bg-gray-800 border-b-2 border-cyan-500">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
              {currentCategory
                ? currentCategory.label.toUpperCase()
                : "ARTICLE ARCHIVES"}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 px-4">
              {currentCategory
                ? `Focused coverage of ${currentCategory.shortLabel.toLowerCase()} from across New Hampshire`
                : "Comprehensive coverage of recovery, unsung heroes, and community stories"}
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row">
              <input
                type="text"
                placeholder="Search articles by title, content, or author..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 border-2 border-gray-600 bg-gray-700 text-white px-4 py-3 text-base md:text-lg focus:border-cyan-500 focus:outline-none placeholder-gray-400 mb-2 sm:mb-0"
              />
              <button className="bg-cyan-500 text-gray-900 px-6 py-3 hover:bg-cyan-400 transition-colors sm:ml-0">
                <span className="font-semibold">SEARCH</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="bg-blue-600 text-white p-4 text-center">
          Loading articles...
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full bg-gray-800 border-2 border-gray-600 text-white p-3 font-semibold flex justify-between items-center"
          >
            <span>FILTER BY CATEGORY</span>
            <svg
              className={`w-5 h-5 transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar - Categories */}
          <aside
            className={`lg:col-span-3 ${
              showFilters ? "block" : "hidden lg:block"
            }`}
          >
            <div className="lg:sticky lg:top-8">
              <h3 className="text-xl md:text-2xl font-bold text-cyan-400 mb-4 border-b-2 border-cyan-500 pb-2">
                CATEGORIES
              </h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveFilter(category.id);
                      setShowFilters(false); // Close mobile filter on selection
                      navigate(
                        category.id !== "all"
                          ? getArticleCategoryUrl(category.id)
                          : "/articles"
                      );
                    }}
                    className={`w-full text-left p-3 border-2 transition-colors ${
                      activeFilter === category.id
                        ? "border-cyan-500 bg-gray-800 text-cyan-400"
                        : "border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm md:text-base">
                        {category.label}
                      </span>
                      <span
                        className={`text-sm px-2 py-1 rounded ${
                          activeFilter === category.id
                            ? "bg-cyan-500 text-gray-900"
                            : "bg-gray-700 text-gray-300"
                        }`}
                      >
                        {category.count}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Quick Stats */}
              <div className="mt-8 bg-gray-800 border border-gray-600 p-4">
                <h4 className="text-lg font-bold mb-3 text-cyan-400">
                  ARCHIVE STATS
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total Articles:</span>
                    <span className="text-cyan-400 font-semibold">
                      {articles.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Contributors:</span>
                    <span className="text-cyan-400 font-semibold">
                      {new Set(articles.map((a) => a.author)).size}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg. Read Time:</span>
                    <span className="text-cyan-400 font-semibold">5 min</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content - Articles */}
          <main className="lg:col-span-9">
            {/* Results Header */}
            <div className="mb-6 border-b border-gray-600 pb-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {activeFilter === "all"
                    ? "All Articles"
                    : categories.find((c) => c.id === activeFilter)?.label}
                  {searchTerm && (
                    <span className="text-cyan-400 block sm:inline">
                      {" "}
                      - Search: "{searchTerm}"
                    </span>
                  )}
                </h2>
                <span className="text-gray-400 text-sm">
                  {filteredArticles.length} article
                  {filteredArticles.length !== 1 ? "s" : ""} found
                </span>
              </div>
            </div>

            {/* Articles Grid */}
            {filteredArticles.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg mb-4">
                  No articles found
                </div>
                <p className="text-gray-500">
                  Try adjusting your search terms or category filter
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {filteredArticles.map((article) => (
                  <article
                    key={article.id}
                    className={`border-b-2 pb-6 ${
                      article.featured ? "border-cyan-500" : "border-gray-600"
                    }`}
                  >
                    <div className="flex flex-wrap items-center mb-3 gap-2">
                      <span
                        className={`${getCategoryColor(
                          article.category
                        )} text-gray-900 px-3 py-1 text-xs md:text-sm font-bold tracking-wider`}
                      >
                        {getCategoryLabel(article.category).toUpperCase()}
                      </span>
                      {article.featured && (
                        <span className="bg-yellow-500 text-gray-900 px-3 py-1 text-xs md:text-sm font-bold tracking-wider">
                          FEATURED
                        </span>
                      )}
                      {article.opinionArticle && (
                        <span className="bg-orange-500 text-gray-900 px-3 py-1 text-xs md:text-sm font-bold tracking-wider">
                          OPINION
                        </span>
                      )}
                      <div className="hidden md:block md:ml-4 h-px bg-gray-600 flex-1"></div>
                      <span className="text-xs md:text-sm text-gray-400 md:ml-4">
                        {article.readTime}
                      </span>
                    </div>

                    <Link
                      to={`/articles/${article.slug || article.id}`}
                      className="block"
                    >
                      <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 leading-tight text-white hover:text-cyan-400 transition-colors cursor-pointer">
                        {article.title}
                      </h3>
                    </Link>

                    {article.imageUrl ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                        <div className="md:col-span-2 order-2 md:order-1">
                          <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-4">
                            {article.excerpt}
                          </p>
                        </div>
                        <div className="md:col-span-1 order-1 md:order-2">
                          <div className="w-full h-56 border-2 border-gray-600 rounded overflow-hidden">
                            <ArticleImage
                              src={article.imageUrl}
                              alt={article.title}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-4">
                        {article.excerpt}
                      </p>
                    )}

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm space-y-2 sm:space-y-0">
                      <div className="text-gray-400">
                        <span className="font-semibold text-cyan-400">
                          By {article.author}
                        </span>
                        <span className="mx-2">|</span>
                        <span>{formatDate(article.date)}</span>
                      </div>
                      <Link
                        to={`/articles/${article.slug || article.id}`}
                        className="text-cyan-400 font-semibold hover:underline text-left sm:text-right"
                      >
                        READ FULL ARTICLE →
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* Load More Button */}
            {filteredArticles.length > 0 && (
              <div className="text-center mt-12">
                <button className="bg-cyan-500 text-gray-900 px-6 md:px-8 py-3 font-semibold hover:bg-cyan-400 transition-colors text-sm md:text-base">
                  LOAD MORE ARTICLES
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-600 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">CONTACT US</h5>
              <p className="text-sm text-gray-300">
                <br />
                Laconia, NH 03246
                <br />
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">SECTIONS</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href="/articles/category/unsung-heroes"
                    className="hover:text-cyan-400"
                  >
                    Healthcare Heroes
                  </a>
                </li>
                <li>
                  <a
                    href="/articles/category/stories-of-recovery"
                    className="hover:text-cyan-400"
                  >
                    Recovery Stories
                  </a>
                </li>
                <li>
                  <a href="/resources" className="hover:text-cyan-400">
                    Resources
                  </a>
                </li>
                <li>
                  <a
                    href="/articles/category/blog-posts"
                    className="hover:text-cyan-400"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">RESOURCES</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href="/resources/category/treatment-centers"
                    className="hover:text-cyan-400"
                  >
                    Treatment Centers
                  </a>
                </li>
                <li>
                  <a
                    href="/resources/category/support-groups"
                    className="hover:text-cyan-400"
                  >
                    Support Groups
                  </a>
                </li>
                <li>
                  <a
                    href="/resources/category/crisis-services"
                    className="hover:text-cyan-400"
                  >
                    Crisis Hotline
                  </a>
                </li>
                <li>
                  <a
                    href="/resources/category/mental-health-providers"
                    className="hover:text-cyan-400"
                  >
                    Mental Health
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">FOLLOW US</h5>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61559004021648"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Recovery Compass NH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Articles;
